import { newLogger } from "@/utils/util";
import network from "../../../utils/network";
import Vue from "vue";
import eventBus, { EVENTS } from "@/utils/eventBus";

let logger = newLogger("ProfileStore");

const CUSTOMER_STATUS_STORAGE_KEY = "fintrous_instacash_status_storage";

const urls = {
  statementAcceptances: `/statements`,
  profile: `/profile`,
  profileUpdate: `/profile/update`,
  statusUpdate: "/profile/status",
  passwordChange: `/bulwark/customer/password-change`,
  isEmailConfirmed: "/bulwark/customer/is-email-confirmed",
  sendConfirmationEmail: "/bulwark/customer/auth/send-confirmation-email",
  customerEmailConfirm: "/customer/email-confirm",
  changeEmail: "/bulwark/customer/auth/change-email",
  confirmEmail: "/bulwark/customer/auth/confirm-email",
  changePhone: "/bulwark/customer/auth/change-phone",
  confirmPhone: "/bulwark/customer/auth/confirm-phone",
};

/**
 * Customer store
 */

const initState = () => {
  let storedStatus = localStorage.getItem(CUSTOMER_STATUS_STORAGE_KEY);
  if (storedStatus) {
    if (storedStatus.includes("IN_FLOW")) {
      let states = storedStatus.split("|");
      return {
        profile: {
          status: states[0],
          flowPosition: states[1],
          flowPage: states[2],
        },
        statementAcceptances: [],
      };
    } else {
      return {
        profile: {
          status: storedStatus,
          flowPosition: null,
          flowPage: null,
        },
        statementAcceptances: [],
      };
    }
  } else {
    return {
      profile: {
        status: null,
        flowPosition: null,
        flowPage: null,
      },
      statementAcceptances: [],
    };
  }
};

const actions = {
  fetch: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.profile);
      commit("saveProfile", data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  save: async ({}, request) => {
    try {
      const axios = await network.connection();
      await axios.put(urls.profileUpdate, request);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  passwordChange: async ({}, request) => {
    try {
      const axios = await network.connection();
      await axios.post(urls.passwordChange, request);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  emailChange: async ({}, request) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.post(urls.changeEmail, request);
      return data.id;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  confirmEmail: async ({}, request) => {
    try {
      const axios = await network.connection();
      await axios.post(urls.confirmEmail, request);
    } catch (e) {
      logger.warn(e);
      return Promise.reject(e);
    }
  },
  phoneChange: async ({}, request) => {
    try {
      const axios = await network.connection();
      const response = await axios.post(urls.changePhone, request);
      return response.data.tokenId;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  confirmPhone: async ({}, request) => {
    try {
      const axios = await network.connection();
      await axios.post(urls.confirmPhone, request);
    } catch (e) {
      logger.warn(e);
      return Promise.reject(e);
    }
  },
  fetchStatementAcceptances: async ({ commit }) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.get(urls.statementAcceptances);
      commit("saveStatementAcceptances", data);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  sendConfirmationEmail: async ({}, {nonce, isLeasing}) => {
    try {
      const axios = await network.connection();
      const { data } = await axios.post(urls.sendConfirmationEmail, { nonce: nonce, isLeasing:isLeasing });
      return data.id;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  setStatus: async ({ commit }, loginResponse) => {
    // called from Login
    let page;
    switch (loginResponse.status) {
      case "REGISTERED":
        page = "/onboarding";
        break;
      case "BNPL_REGISTERED":
        page = "/onboarding";
        break;
      case "LEASING_REGISTERED":
        page = "/leasing/prescore";
        break;
      case "ONBOARDED":
        page = "/preliminary-assessment";
        break;
      case "LEASING_ONBOARDED":
        page = "/leasing/prescore";
        break;
      case "PRELIMINARY":
        page = "/offers";
        break;
      case "LEASING_PRESCORE":
        page = "/leasing/prescore";
        break;
      case "IN_FLOW":
        page = "/application";
        break;
      case "CLOSED":
        page = "/403";
        break;
      default:
        page = "/";
        break;
    }
    commit("saveLoginStatus", loginResponse);

    return page;
  },
  updateStatus: async ({ commit }, { status, result }) => {
    try {
      let axios = await network.connection();
      await axios.put(urls.statusUpdate, {
        currentStatus: status,
        currentStatusResult: result,
      });
      commit("saveStatus", status);
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  isEmailConfirmed: async () => {
    try {
      let axios = await network.connection();
      let response = await axios.get(urls.isEmailConfirmed);
      return response.data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  updateCustomerEmailConfirm: async ({}, { offerId }) => {
    try {
      let axios = await network.connection();
      await axios.patch(urls.customerEmailConfirm, {
        offerId: offerId,
      });
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
  getEmailConfirmationData: async () => {
    try {
      let axios = await network.connection();
      let { data } = await axios.get(urls.customerEmailConfirm);
      return data;
    } catch (err) {
      logger.error(err);
      await Promise.reject(err);
    }
  },
};

const mutations = {
  saveProfile: (state, data) => {
    state.profile = data;
    state.profile.status = data.currentStatus;
    state.profile.statusResult = data.currentStatusResult;
  },
  saveStatementAcceptances: (state, data) => {
    Vue.set(state, "statementAcceptances", data);
  },
  saveLoginStatus: (state, response) => {
    Vue.set(state.profile, "status", response.status);
    Vue.set(state.profile, "flowPosition", response.flowPosition);
    Vue.set(state.profile, "flowPage", response.flowPage);
    if (response.status.includes("IN_FLOW")) {
      localStorage.setItem(
        CUSTOMER_STATUS_STORAGE_KEY,
        `${response.status}|${response.flowPosition}|${response.flowPage}`
      );
    } else {
      localStorage.setItem(CUSTOMER_STATUS_STORAGE_KEY, response.status);
    }
  },
  saveStatus: (state, status) => {
    Vue.set(state.profile, "status", status);
    localStorage.setItem(CUSTOMER_STATUS_STORAGE_KEY, status);
    eventBus.$emit(EVENTS.STATUS_UPDATED);
  },
};

const getters = {
  get: (state) => {
    return state.profile;
  },
  getStatementAcceptances: (state) => {
    return state.statementAcceptances;
  },
  hasActiveApplication: (state) => {
    return state.profile.status === "IN_FLOW";
  },
  isInState: (state) => (desired) => {
    return state.profile.status === desired;
  },
  getBackPath: (state) => {
    switch (state.profile.status) {
      case "REGISTERED":
        return "/onboarding";
      case "LEASING_REGISTERED":
        return "/leasing/prescore";
      case "BNPL_REGISTERED":
        return "/onboarding";
      case "ONBOARDED":
        return "/preliminary-assessment";
      case "LEASING_ONBOARDED":
        return "/leasing/prescore";
      case "PRELIMINARY":
        return "/offers";
      case "LEASING_PRESCORE":
        return "/leasing/prescore";
      case "IN_FLOW":
        return "/application";
    }
  },
};

export default {
  namespaced: true,
  state: initState(),
  mutations: mutations,
  actions: actions,
  getters: getters,
};
